<template>
  <div class="chart-container">
    <!-- Custom content overlay -->
    <div class="chart-overlay" ref="overlay">
      <div class="overlay-content">
        <!-- Labels column -->
        <div class="labels-column">
          <div class="header-row">
            <span class="header-label">Terjual</span>
          </div>
          <div class="customer-row">
            <span class="customer-label">Customer</span>
          </div>
        </div>

        <!-- Content area -->
        <div class="content-area">
          <!-- Header row with tonnage badges -->
          <div class="header-row">
            <div v-for="(item, index) in overlayItems" :key="index" class="tonnage-item" :style="getItemStyle(index)">
              <span class="tonnage-badge">{{ item.tonnage }}</span>
            </div>
          </div>

          <!-- Customer row with customer badges -->
          <div class="customer-row">
            <div v-for="(item, index) in overlayItems" :key="index" class="customer-item" :style="getItemStyle(index)">
              <span class="customer-badge">{{ item.customers }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart wrapper -->
    <div class="chart-wrapper">
      <BarChart ref="chart" :chart-data="chartData" :chart-options="chartOptions" @chart:render="onChartRender" />
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
  name: 'GroupedStackedBarChart',
  props: ['overlayItems', 'chartData'],
  components: {
    BarChart: Bar
  },
  data() {
    return {
      itemPositions: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 72
          }
        },
        scales: {
          x: {
            ticks: {
              maxRotation: 90,
              minRotation: 90,
              autoSkip: false,
            },
            grid: {
              display: false
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              display: false
            },
            grid: {
              display: true
            }
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            align: 'start',
            labels: {
              font: {
                size: 14
              },
              // Customize the legend box dimensions
              boxWidth: 13,
            }
          },
          datalabels: {
            color: '#fff',
            font: {
              size: 10
            },
            formatter: (value) => {
              return value
            }
          }
        },
      },
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.calculatePositions()
    })
    window.addEventListener('resize', this.calculatePositions)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculatePositions)
  },
  methods: {
    onChartRender() {
      this.$nextTick(() => {
        this.calculatePositions()
      })
    },
    calculatePositions() {
      if (!this.$refs.chart || !this.$refs.chart.chart) return

      const chart = this.$refs.chart.chart
      const chartArea = chart.chartArea
      const labels = chart.data.labels

      if (!chartArea) return

      // Account for the labels column width (80px)
      const labelsColumnWidth = 80

      this.itemPositions = labels.map((label, index) => {
        const barWidth = chartArea.width / labels.length
        const barCenter = chartArea.left + (barWidth * index) + (barWidth / 2) - labelsColumnWidth

        return {
          left: barCenter - (barWidth * 0.4) / 2,
          width: barWidth * 0.4
        }
      })
    },
    getItemStyle(index) {
      if (!this.itemPositions[index]) return { opacity: 0 }

      const position = this.itemPositions[index]
      return {
        position: 'absolute',
        left: `${position.left}px`,
        width: `${position.width}px`,
        display: 'flex',
        justifyContent: 'center'
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  margin-left: auto;
  width: 100%;
  padding-top: 80px;
  /* Space for overlay */
}

.chart-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  z-index: 10;
  pointer-events: none;
}

.overlay-content {
  position: relative;
  height: 100%;
  display: flex;
}

.labels-column {
  width: 80px;
  flex-shrink: 0;
  /* border-right: 1px solid #e0e0e0; */
}

.labels-column .header-row {
  height: 40px;
  display: flex;
  /* margin-left: -75px !important; */
  align-items: center;
  justify-content: start;
  /* border-bottom: 1px solid #e0e0e0; */
}

.labels-column .customer-row {
  height: 40px;
  display: flex;
  /* margin-left: -75px !important; */
  align-items: center;
  justify-content: start;
}

.content-area {
  flex: 1;
  position: relative;
}

.content-area .header-row {
  position: relative;
  height: 40px;
  /* border-bottom: 1px solid #e0e0e0; */
}

.content-area .customer-row {
  position: relative;
  height: 40px;
}

.header-label,
.customer-label {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.tonnage-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tonnage-badge {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
}

.customer-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-badge {
  background: #007bff;
  color: white;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
}

.chart-wrapper {
  margin-top: 1rem;
}
.chart-wrapper div {
  position: relative;
  height: 400px;
}

canvas {
  max-height: 600px;
}
</style>
